import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function LogCallDialog({ lead, open, onOpenChange }) {
  const qc = useQueryClient();
  const [parent, setParent] = useState("");
  const [sub, setSub] = useState("__none");
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");

  const { data: disp } = useQuery({ queryKey: ["dispositions-all"], queryFn: async () => (await api.get("/dispositions/all")).data, enabled: open });

  const { parents, subsByParent } = useMemo(() => {
    const builtin = disp?.built_in || [];
    const custom = disp?.custom || [];
    const parentMap = new Map();
    builtin.filter((b) => b.active !== false).forEach((b) => parentMap.set(b.key, b.label));
    custom.forEach((c) => { if (c.parent_key && !parentMap.has(c.parent_key)) parentMap.set(c.parent_key, c.parent_key.replace("builtin:", "")); else if (!c.parent_key && c.active !== false) parentMap.set(c.key, c.label); });
    const subs = {};
    custom.filter((c) => c.active !== false).forEach((c) => { (subs[c.parent_key] = subs[c.parent_key] || []).push(c.label); });
    return { parents: Array.from(parentMap, ([key, label]) => ({ key, label })), subsByParent: subs };
  }, [disp]);

  const parentKey = parents.find((p) => p.label === parent)?.key;
  const subs = (parentKey && subsByParent[parentKey]) || [];
  const isFollowUp = parent === "Follow-up";

  const mutate = useMutation({
    mutationFn: async () => {
      const disposition = sub !== "__none" ? sub : parent;
      const body = { lead_id: lead.id, disposition, parent_disposition: parent, notes };
      if (parentKey) body.parent_key = parentKey;
      if (followUp) body.follow_up_at = new Date(followUp).toISOString();
      return (await api.post(`/leads/${lead.id}/call`, body)).data;
    },
    onSuccess: () => {
      ["leads", "lead-calls", "dashboard-stats", "followups"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success("Call logged");
      onOpenChange(false);
      setParent(""); setSub("__none"); setNotes(""); setFollowUp("");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white dark:bg-slate-900" data-testid="log-call-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Log Call — {lead?.name}</DialogTitle>
          <DialogDescription>Record the outcome of this call and set a follow-up.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Disposition *</Label>
            <Select value={parent} onValueChange={(v) => { setParent(v); setSub("__none"); }}>
              <SelectTrigger data-testid="call-disposition-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
              <SelectContent>{parents.map((p) => <SelectItem key={p.key} value={p.label}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {subs.length > 0 && (
            <div className="space-y-2">
              <Label>Sub-disposition</Label>
              <Select value={sub} onValueChange={setSub}>
                <SelectTrigger data-testid="call-subdisposition-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__none">— None —</SelectItem>{subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Next Follow-up {isFollowUp && <span className="text-rose-500">*</span>}</Label>
            <Input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="call-followup-input" />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call notes (optional)" data-testid="call-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="call-save-button"
            disabled={!parent || (isFollowUp && !followUp) || mutate.isPending}
            onClick={() => mutate.mutate()}>Save Call</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
